// ─── ISO date helpers (UTC-safe) ──────────────────────────────────────────────
//
// The scheduling engine works exclusively in plain ISO dates (YYYY-MM-DD) so
// day-offset math never drifts across timezones or DST. Conversion to the
// Temporal objects Schedule-X needs happens only at the calendar boundary.

/** Format a UTC Date as an ISO date string (YYYY-MM-DD). */
export function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** Parse an ISO date string (YYYY-MM-DD) into a UTC midnight Date. */
export function parseISO(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

/**
 * Return the ISO date `days` after (or before, if negative) the given date.
 * This engine only represents whole calendar days; `days` can now arrive
 * fractional (procedure lag windows support Days+Hours, ticket 86ey3wurt),
 * so round explicitly here rather than relying on `setUTCDate`'s implicit
 * truncation-toward-zero, which would silently shave off the fractional part
 * (e.g. a 1.5-day lag landing a half-day earlier than configured).
 */
export function addDays(iso: string, days: number): string {
  const d = parseISO(iso)
  d.setUTCDate(d.getUTCDate() + Math.round(days))
  return toISODate(d)
}

/** Whole-day difference `toIso - fromIso` (can be negative). */
export function daysBetween(fromIso: string, toIso: string): number {
  const a = parseISO(fromIso).getTime()
  const b = parseISO(toIso).getTime()
  return Math.round((b - a) / 86_400_000)
}

/**
 * Start of the week containing `iso`. `firstDayOfWeek` is 0=Sun … 6=Sat,
 * defaulting to Monday (1) to match the calendar's `firstDayOfWeek`.
 */
export function startOfWeek(iso: string, firstDayOfWeek = 1): string {
  const day = parseISO(iso).getUTCDay()
  const diff = (day - firstDayOfWeek + 7) % 7
  return addDays(iso, -diff)
}

/** The seven ISO dates of the week beginning at `weekStartIso`. */
export function weekDays(weekStartIso: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStartIso, i))
}

/** Human-readable label, e.g. "Tue, Jul 9". */
export function formatShortDate(iso: string): string {
  return parseISO(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/** Three-letter weekday, e.g. "Tue". */
export function formatWeekday(iso: string): string {
  return parseISO(iso).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })
}

/** Day of month as a string, e.g. "9". */
export function formatDayNum(iso: string): string {
  return String(parseISO(iso).getUTCDate())
}

/** True for Saturday / Sunday. */
export function isWeekend(iso: string): boolean {
  const day = parseISO(iso).getUTCDay()
  return day === 0 || day === 6
}

/** Compact inclusive range, e.g. "Jul 9 – 11" or "Jul 30 – Aug 1". */
export function formatDateRange(fromIso: string, toIso: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' }
  const from = parseISO(fromIso)
  const to = parseISO(toIso)
  const fromStr = from.toLocaleDateString('en-US', opts)
  if (fromIso === toIso) return fromStr
  const sameMonth = from.getUTCMonth() === to.getUTCMonth()
  const toStr = to.toLocaleDateString('en-US', sameMonth ? { day: 'numeric', timeZone: 'UTC' } : opts)
  return `${fromStr} – ${toStr}`
}

/** Today as an ISO date (UTC). */
export function todayISO(): string {
  return toISODate(new Date())
}
